// src/features/system/developer/components/video-gallery/telemetry.ts
// ────────────────────────────────────────────────────────────────────────────
// Structured FE telemetry for the video feed.
//
// Goals:
//   • One canonical schema for every feed event (slide_view, play_start,
//     recovery_retry, error, etc.). Enables grep + alerting in the log
//     aggregator without bespoke parsing per call site.
//   • Best-effort fire-and-forget — telemetry MUST NEVER throw into the
//     render path. Network failures, missing endpoints, blocked
//     analytics — all silently dropped.
//   • Per-event sampling so we don't drown the BFF in `slide_view` chatter
//     while still capturing 100% of errors and recoveries.
//   • Local debug toggle via localStorage('debug:video-telemetry') = '1'
//     for development without flipping a server flag.
//
// All events are buffered through `sendBeacon` when available so they
// survive page unloads / route changes without blocking navigation.
// ────────────────────────────────────────────────────────────────────────────

export type FeedEventKind =
  | 'slide_view'
  | 'play_start'
  | 'play_pause'
  | 'buffering_start'
  | 'buffering_end'
  | 'recovery_retry'
  | 'recovery_failed'
  | 'error'
  | 'orphan_dismissed'
  | 'mute_toggle'
  | 'keyboard_nav'
  // List-level robustness signals emitted by the anti-shrink merge
  // guard in `useVideoGallery.useAntiShrinkItems`. They power SLO
  // dashboards for backend-wobble visibility (BFF cache wobble,
  // cross-Cloud-Run-instance variance, non-authoritative served_from
  // sources). FE-only events — never affect render correctness.
  | 'list_shrink_detected'   // fresh < prior * tolerance
  | 'list_carry_engaged'     // anti-shrink merge actively carried prior rows
  | 'list_stale_served';     // BFF flagged the payload as non-authoritative

interface FeedEvent {
  kind: FeedEventKind;
  /** Canonical SKID-or-local id of the video (whichever the feed has). */
  video_id?: string;
  /** Active slide index at event time. */
  index?: number;
  /** Free-form structured detail (kept flat for log indexing). */
  detail?: Record<string, string | number | boolean | null | undefined>;
}

/** Per-kind sample rate. 1.0 = always send. 0.0 = never. */
const SAMPLE_RATE: Record<FeedEventKind, number> = {
  slide_view:        0.1,  // chatty — sample to 10%
  play_start:        1.0,
  play_pause:        0.2,
  buffering_start:   1.0,
  buffering_end:     0.5,
  recovery_retry:    1.0,
  recovery_failed:   1.0,
  error:             1.0,
  orphan_dismissed:  1.0,
  mute_toggle:       0.1,
  keyboard_nav:      0.05,
  // List-level robustness events are rare by design (only fire when
  // the guard engages or stale_served flips). Capture every one.
  list_shrink_detected: 1.0,
  list_carry_engaged:   1.0,
  list_stale_served:    1.0,
};

function isDebug(): boolean {
  try {
    return typeof localStorage !== 'undefined'
      && localStorage.getItem('debug:video-telemetry') === '1';
  } catch { return false; }
}

function shouldEmit(kind: FeedEventKind): boolean {
  if (isDebug()) return true;
  const rate = SAMPLE_RATE[kind] ?? 1.0;
  if (rate >= 1.0) return true;
  if (rate <= 0.0) return false;
  return Math.random() < rate;
}

const ENDPOINT = '/bff/services/video-pipeline/analytics/feed';

function isSharedRoute(): boolean {
  const path = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
  return path.startsWith('/s/');
}

/**
 * Emit one feed event. Fire-and-forget, never throws, never blocks.
 */
export function emitFeedEvent(ev: FeedEvent): void {
  // Shared links are anonymous/public by design; avoid auth-bound telemetry.
  if (isSharedRoute()) return;
  if (!shouldEmit(ev.kind)) return;

  const payload = {
    ts: Date.now(),
    ...ev,
  };

  if (isDebug()) {
    // eslint-disable-next-line no-console
    console.debug('[video-feed:telemetry]', payload);
  }

  try {
    // Prefer sendBeacon so navigation/unload doesn't drop the event.
    const body = JSON.stringify(payload);
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (nav && typeof nav.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = nav.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }
    // Fallback: fetch with keepalive. No await — best-effort.
    if (typeof fetch === 'function') {
      void fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'include',
      }).catch(() => { /* ignore */ });
    }
  } catch {
    // Network blocked / CSP / offline — drop silently.
  }
}
